import { useNavigate, useSearch } from '@tanstack/react-router';
import { StrategyChartHistory } from 'components/strategies/common/StrategyChartHistory';
import { StrategyChartSection } from 'components/strategies/common/StrategyChartSection';
import { useStrategyFormCtx } from 'components/strategies/common/StrategyFormContext';
import {
  StrategyDirection,
  TradeAuctionSearch,
} from 'libs/routing/routes/trade';
import { useCallback, useMemo } from 'react';
import { D3EditLine } from 'components/strategies/common/d3Chart/drawing/D3DrawLine';
import { D3DrawingRanges } from 'components/strategies/common/d3Chart/drawing/D3DrawingRanges';
import { useGradientOrder } from 'components/strategies/common/gradient/useGradientOrder';
import { CreateGradientOrder } from 'components/strategies/common/gradient/CreateGradientOrder';
import {
  defaultGradientMultipliers,
  defaultGradientOrder,
} from 'components/strategies/common/gradient/utils';
import { CreateGradientStrategyForm } from 'components/strategies/common/gradient/CreateGradientStrategyForm';
import { OrderDirection } from 'components/strategies/common/OrderDirection';
import { TradeChartContent } from 'components/strategies/common/d3Chart/TradeChartContent';
import { emptyGradientOrder } from 'components/strategies/common/utils';
import { EditMarketPrice } from 'components/strategies/common/InitMarketPrice';
import { CreateLayout } from 'components/strategies/create/CreateLayout';
import { D3ChartToday } from 'components/strategies/common/d3Chart/D3ChartToday';
import { cn } from 'utils/helpers';
import style from 'components/strategies/common/order.module.css';

const url = '/trade/auction';
export const TradeAuction = () => {
  const { base, quote, marketPrice } = useStrategyFormCtx();
  const search = useSearch({ from: url });
  const navigate = useNavigate({ from: url });

  const saveOrder = useCallback(
    (next: TradeAuctionSearch) => {
      navigate({
        params: (params) => params,
        search: (previous) => ({ ...previous, ...next }),
        replace: true,
        resetScroll: false,
      });
    },
    [navigate],
  );

  const direction = search.direction ?? 'sell';
  const multi = useMemo(() => {
    return defaultGradientMultipliers(base.address, quote.address, direction);
  }, [base.address, quote.address, direction]);

  const initOrder = useMemo(() => {
    return defaultGradientOrder(search, multi, marketPrice);
  }, [search, multi, marketPrice]);

  const { order, setOrder, drawing, onDrawingUpdate } = useGradientOrder(
    initOrder,
    saveOrder,
  );

  const orders = {
    buy: direction === 'buy' ? order : emptyGradientOrder(),
    sell: direction === 'sell' ? order : emptyGradientOrder(),
  };

  const setDirection = useCallback(
    (direction: StrategyDirection) => {
      saveOrder({
        direction,
        startPrice: undefined,
        endPrice: undefined,
        budget: undefined,
      });
    },
    [saveOrder],
  );

  return (
    <>
      <StrategyChartSection
        editMarketPrice={<EditMarketPrice base={base} quote={quote} />}
      >
        <StrategyChartHistory direction={direction} {...orders}>
          <D3EditLine
            drawing={drawing}
            color={direction}
            onChange={onDrawingUpdate}
          />
          <TradeChartContent />
          <D3ChartToday />
          <D3DrawingRanges drawing={drawing} color={direction} />
        </StrategyChartHistory>
      </StrategyChartSection>
      <CreateLayout url={url}>
        <CreateGradientStrategyForm buy={orders.buy} sell={orders.sell}>
          <div className="surface grid rounded-2xl overflow-clip">
            <OrderDirection direction={direction} setDirection={setDirection} />
            <div
              className={cn(style.order, 'grid gap-16 p-16')}
              data-direction={direction}
              data-disposable={true}
            >
              <CreateGradientOrder order={order} setOrder={setOrder} />
            </div>
          </div>
        </CreateGradientStrategyForm>
      </CreateLayout>
    </>
  );
};
